// Activities & actions — the header work center. "Actions" are what the person still has to do
// (prominent, one call to action each); "Activity" is what already happened (a compact timeline).
// Data comes from the same workflow endpoints the previous work center used.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UiIcon from "../components/UiIcon";
import { apiRequest } from "./api";
import { experienceCopy, dueLabel, formatNumber, localeOf } from "./copy";
import { buildNextSteps } from "./DashboardPanels";
import { formatStamp, workspaceCopy } from "./workspaceCopy";
import { ActionItem, ActivityFeed, ActivityItem, DetailDrawer, FilterTabs, GroupLabel, SkeletonRows, WsEmpty } from "./WorkspaceUi";
import { EvaluationDutiesView, useEvaluationDuties } from "./EvaluationDuties";
import { evaluationResponsibilityCopy } from "../components/routine/evaluationResponsibilityCopy";

const MODULE_ICON = { routine: "self", activity: "activity", contribution: "activity", peer: "peer", indicator: "target", goal: "target", period: "schedule", approval: "inbox", support: "warning", review: "flag", "check-in": "schedule" };
const ACTIONABLE = ["overdue", "due_soon", "my_action"];
const STATE_ORDER = ["overdue", "due_soon", "my_action", "waiting", "info"];

// Primary call-to-action wording for each task type.
const CTA_KIND = {
    ACTIVITY_REVIEW: "review", INDICATOR_APPROVAL: "approve", GOAL_APPROVAL: "approve", ROUTINE_EVALUATION: "review",
    PEER_FEEDBACK: "give", SUPPORT_RESPONSE: "respond", REVIEW_ACKNOWLEDGEMENT: "acknowledge",
    SELF_ASSESSMENT: "complete", ACTIVITY_DRAFT: "complete", ACTIVITY_REVISION: "complete", INDICATOR_ENTRY: "complete", GOAL_PROGRESS: "complete",
};

const EVENT_TONE = { approved: "success", rejected: "danger", revision_requested: "warning", submitted: "info", resubmitted: "info", self_assessment_submitted: "info" };
// Server titles for these modules are generic English placeholders; the module label is used instead.
const NAMED_ENTITY_MODULES = ["activity", "indicator", "goal"];

const formatClock = (value, language) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : new Intl.DateTimeFormat(localeOf(language), { hour: "numeric", minute: "2-digit" }).format(date);
};

const dayKey = value => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

export function useWorkCenterData(periodId, enabled) {
    const key = periodId || "";
    const [state, setState] = useState({ key: null, tasks: null, events: null, error: false });
    const [loading, setLoading] = useState(false);
    const latestKey = useRef(key);
    latestKey.current = key;
    const load = useCallback(async () => {
        if (!enabled) return;
        const query = periodId ? `?periodId=${encodeURIComponent(periodId)}` : "";
        const requested = key;
        setLoading(true);
        try {
            const [tasks, history] = await Promise.all([
                apiRequest(`/api/workflow/tasks${query}`),
                apiRequest(`/api/workflow/history${query}${query ? "&" : "?"}limit=100`),
            ]);
            if (latestKey.current !== requested) return;
            setState({ key: requested, tasks, events: history.events || [], error: false });
        } catch (error) {
            console.error("Work center load failed:", error);
            if (latestKey.current === requested) setState(prev => ({ ...prev, key: prev.key === requested ? prev.key : null, error: true }));
        } finally { setLoading(false); }
    }, [enabled, periodId, key]);
    useEffect(() => {
        if (!enabled) return undefined;
        load();
        const timer = setInterval(load, 60000);
        return () => clearInterval(timer);
    }, [enabled, load]);
    // Data belongs to one cycle: never show another cycle's tasks while the new ones load.
    const fresh = state.key === key;
    return { tasks: fresh ? state.tasks : null, events: fresh ? state.events : null, error: state.error && !fresh, loading, reload: load };
}

export function WorkCenter({ language, periodId, navigator }) {
    const [open, setOpen] = useState(false);
    const { tasks: payload, events, error, loading, reload } = useWorkCenterData(periodId, true);
    const t = workspaceCopy(language);
    const steps = useMemo(() => buildNextSteps(payload), [payload]);
    const actionable = steps.filter(task => ACTIONABLE.includes(task.state || "my_action"));
    const count = actionable.length;
    return <>
        <button type="button" className="cvi-icon-button" aria-label={count ? `${t.workCenter.title} (${count})` : t.workCenter.title} title={t.workCenter.title}
            onClick={() => { setOpen(true); reload(); }} style={{ position: "relative" }} data-cvi-i18n="managed">
            <UiIcon name="bell" size={17} />
            {count > 0 && <span className="cvi-ws-bell-count" aria-hidden="true">{count > 99 ? "99+" : count}</span>}
        </button>
        {open && <WorkCenterDrawer language={language} steps={steps} events={events} loaded={payload != null} error={error} loading={loading}
            onRetry={reload} onClose={() => setOpen(false)} navigator={navigator} periodId={periodId} />}
    </>;
}

function WorkCenterDrawer({ language, steps, events, loaded, error, loading, onRetry, onClose, navigator, periodId }) {
    const t = workspaceCopy(language);
    const x = experienceCopy(language);
    const er = evaluationResponsibilityCopy(language);
    const [tab, setTab] = useState("actions");
    // Loaded only once the tab is opened; the bell count stays driven by actions.
    const duties = useEvaluationDuties(periodId, tab === "duties");
    const actionable = steps.filter(task => ACTIONABLE.includes(task.state || "my_action"));
    const overdue = steps.filter(task => task.state === "overdue").length;
    const moduleLabel = module => t.modules[module] || module;
    const canOpen = target => Boolean(target && navigator?.can?.(target));
    const go = task => { if (navigator?.open?.(task.actionTarget, task) !== false) onClose(); };

    const actionsView = () => {
        if (!loaded) return error ? <WsEmpty tone="error" icon="warning" title={t.workCenter.error} action={<button type="button" className="cvi-ws-btn is-secondary is-sm" onClick={onRetry}>{x.common.retry}</button>} />
            : <SkeletonRows count={4} label={t.common.loading} />;
        if (!steps.length) return <WsEmpty title={t.workCenter.emptyActions[0]} description={t.workCenter.emptyActions[1]} />;
        // The single most urgent action gets the solid button; the rest stay secondary so the list stays calm.
        const leadId = steps.find(task => ACTIONABLE.includes(task.state || "my_action") && canOpen(task.actionTarget))?.id;
        return STATE_ORDER.filter(state => steps.some(task => (task.state || "my_action") === state)).map(state => {
            const rows = steps.filter(task => (task.state || "my_action") === state);
            return <section key={state} className="cvi-ws-wc-group" aria-label={t.workCenter.groups[state]}>
                <GroupLabel count={formatNumber(rows.length, language)}>{t.workCenter.groups[state]}</GroupLabel>
                <div className="cvi-ws-actions-list">
                    {rows.map(task => {
                        const params = task.params || {};
                        const title = x.nextSteps.types[task.type]?.(params, task) || task.title;
                        const detail = x.nextSteps.details[task.type]?.(params, task) ?? task.description;
                        const module = moduleLabel(task.category || task.module);
                        const context = [title.startsWith(module) ? null : module, task.subjectName && !title.includes(task.subjectName) ? t.workCenter.forPerson(task.subjectName) : null].filter(Boolean).join(" · ");
                        const isAction = ACTIONABLE.includes(state);
                        const due = task.dueAt ? (state === "info" ? formatStamp(task.dueAt, language) : dueLabel(task.dueAt, language)) : null;
                        const cta = canOpen(task.actionTarget) ? (isAction ? t.workCenter.cta[CTA_KIND[task.type] || "open"] : t.workCenter.cta.open) : null;
                        return <ActionItem key={task.id} language={language} icon={MODULE_ICON[task.category] || MODULE_ICON[task.module] || "activity"}
                            title={title} context={context} detail={detail} due={due}
                            dueTone={state === "overdue" ? "danger" : state === "due_soon" ? "warning" : null}
                            status={state} priority={task.priority} cta={cta} ctaVariant={task.id === leadId ? "primary" : "secondary"} onCta={() => go(task)} />;
                    })}
                </div>
            </section>;
        });
    };

    const activityView = () => {
        if (events == null) return error ? <WsEmpty tone="error" icon="warning" title={t.workCenter.error} action={<button type="button" className="cvi-ws-btn is-secondary is-sm" onClick={onRetry}>{x.common.retry}</button>} />
            : <SkeletonRows count={5} variant="feed" label={t.common.loading} />;
        if (!events.length) return <WsEmpty tone="neutral" icon="history" title={t.workCenter.emptyActivity[0]} description={t.workCenter.emptyActivity[1]} />;
        const days = [];
        for (const event of events) {
            const key = dayKey(event.createdAt);
            const last = days[days.length - 1];
            if (last && last.key === key) last.rows.push(event); else days.push({ key, at: event.createdAt, rows: [event] });
        }
        return days.map(day => <div key={day.key || "unknown"}>
            <h3 className="cvi-ws-wc-day">{formatStamp(day.at, language, { time: false })}</h3>
            <ActivityFeed label={t.workCenter.tabs.activity}>
                {day.rows.map(event => {
                    const type = String(event.eventType || "").toLowerCase();
                    const verb = t.events[type] || t.events.fallback;
                    const module = moduleLabel(event.module);
                    const entity = NAMED_ENTITY_MODULES.includes(event.module) && event.title ? `“${event.title}”` : null;
                    const subject = event.subjectName && event.subjectName !== event.actorName ? event.subjectName : null;
                    const clock = formatClock(event.createdAt, language);
                    return <ActivityItem key={event.id} icon={MODULE_ICON[event.module] || "activity"} tone={EVENT_TONE[type] || "neutral"}
                        actor={event.actorName || t.common.system} action={verb} entity={entity}
                        context={[module, subject].filter(Boolean).join(" · ")} time={clock} note={event.note || null} />;
                })}
            </ActivityFeed>
        </div>);
    };

    return <DetailDrawer open onClose={onClose} language={language} width="md" title={t.workCenter.title}
        headerExtra={loaded ? <span className="cvi-ws-wc-summary">{t.workCenter.summary.actions(actionable.length)}{overdue > 0 && <span className="is-danger">· {t.workCenter.summary.overdue(overdue)}</span>}</span> : null}>
        <FilterTabs label={t.workCenter.title} value={tab} onChange={setTab} options={[
            { id: "actions", label: t.workCenter.tabs.actions, count: loaded ? actionable.length : undefined },
            { id: "duties", label: er.ledger.tab, count: duties.data?.totals ? duties.data.totals.open : undefined },
            { id: "activity", label: t.workCenter.tabs.activity },
        ]} />
        <div aria-busy={loading && !loaded ? "true" : undefined}>{tab === "actions" ? actionsView()
            : tab === "duties" ? <EvaluationDutiesView language={language} data={duties.data} error={duties.error} onRetry={duties.reload} navigator={navigator} onNavigate={onClose} />
            : activityView()}</div>
    </DetailDrawer>;
}
